import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { glassVars } from "@/lib/glass/style";

export type GlassMaterial = "clear" | "regular" | "thick";
export type GlassScheme = "auto" | "light" | "dark";

export interface GlassCardProps extends HTMLAttributes<HTMLElement> {
  /** clear (small controls), regular (bars, cards; default) or thick (menus, dialogs). */
  material?: GlassMaterial;
  /** "auto" follows the page theme; "light" / "dark" pin the scheme for a fixed backdrop. */
  scheme?: GlassScheme;
  /** Optional per-instance tint (0..1) and frost (px). */
  tint?: number;
  frost?: number;
  as?: "div" | "section" | "article" | "aside" | "form";
  children?: ReactNode;
}

/**
 * A layered glass surface (see src/styles/glass.css). The portal's studio
 * cards get the same material through their own classes; this component
 * is for surfaces outside the studio shell (sign-in, contact page).
 * Server-safe: no hooks.
 */
export default function GlassCard({
  material = "regular",
  scheme = "auto",
  tint,
  frost,
  as: Tag = "div",
  className,
  style,
  children,
  ...rest
}: GlassCardProps) {
  const cls = [
    "glass",
    `glass-${material}`,
    scheme === "dark" && "g-on-dark",
    scheme === "light" && "g-on-light",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const merged: CSSProperties | undefined = tint != null || frost != null ? { ...glassVars({ tint, frost }), ...style } : style;
  return (
    <Tag className={cls} style={merged} {...rest}>
      {children}
    </Tag>
  );
}
